import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Icon, type IconName } from './Icon';
import s from './navigation.module.css';

export interface TabZiel {
  an: string;
  label: string;
  icon: IconName;
  ende?: boolean;
}

/**
 * Haupt-Tabs in fester Reihenfolge (GL-023 1.6.0 §9.8). Kurzlabels immer ("Rezepte", "Woche"), die Seitentitel
 * bleiben "Meine Rezepte" und "Meine Woche". Mein Tag steht als sechster Tab immer ganz rechts und nur, wenn die
 * Person die Kalorien-Uebersicht im Profil an hat.
 */
export const TABS: readonly TabZiel[] = [
  { an: '/', label: 'Start', icon: 'start', ende: true },
  { an: '/entdecken', label: 'Entdecken', icon: 'entdecken' },
  { an: '/rezepte', label: 'Rezepte', icon: 'rezepte' },
  { an: '/woche', label: 'Woche', icon: 'woche' },
  { an: '/einkaufen', label: 'Einkaufen', icon: 'einkaufen' },
];
export const TAB_MEIN_TAG: TabZiel = { an: '/mein-tag', label: 'Mein Tag', icon: 'besteck' };

/** Tab-Leiste: aktiv dreifach getragen (Farbe, Gewicht, Punkt), aria-current setzt NavLink. */
export function TabLeiste({ meinTag = false }: { meinTag?: boolean }) {
  const tabs = meinTag ? [...TABS, TAB_MEIN_TAG] : TABS;
  return (
    <nav aria-label="Hauptnavigation" className={s.tableiste}>
      <div className={s.tabInnen}>
        {tabs.map((t) => (
          <NavLink key={t.an} to={t.an} end={t.ende} className={s.tab}>
            {({ isActive }) => (
              <>
                <Icon name={t.icon} groesse={24} strich={isActive ? 1.9 : 1.8} />
                <span className={s.tabLabel}>{t.label}</span>
                {isActive && <span aria-hidden="true" className={s.punkt} />}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/** Kopfzeile einer Tab-Seite: Seitentitel links, runde Kopf-Knoepfe rechts. */
export function Seitenkopf({
  titel,
  unterzeile,
  aktionen,
}: {
  titel: string;
  unterzeile?: ReactNode;
  aktionen?: ReactNode;
}) {
  return (
    <>
      <header className={s.kopf}>
        <h1 className={s.titel}>{titel}</h1>
        {aktionen && <div className={s.kopfAktionen}>{aktionen}</div>}
      </header>
      {unterzeile && <p className={s.unterzeile}>{unterzeile}</p>}
    </>
  );
}

/** Wortmarke "nebenbei" mit Akzentpunkt. */
export function Wortmarke({ gross = false, alsLink = true }: { gross?: boolean; alsLink?: boolean }) {
  const inhalt = (
    <>
      <span className={s.wort}>nebenbei</span>
      <span aria-hidden="true" className={s.wortPunkt} />
    </>
  );
  const k = `${s.wortmarke} ${gross ? s.wortmarkeGross : ''}`;
  return alsLink ? (
    <Link to="/" aria-label="nebenbei, zur Startseite" className={k}>
      {inhalt}
    </Link>
  ) : (
    <div className={k}>{inhalt}</div>
  );
}

/** Kopf der Startseite: Wortmarke links, Platz fuer den Profilknopf rechts. */
export function StartKopf({ rechts }: { rechts: ReactNode }) {
  return (
    <header className={s.kopf}>
      <Wortmarke />
      {rechts}
    </header>
  );
}
